import { injectExternalResources } from '@mfec/lit/injectExternalResources';
import { loadFontsIntoLightDom } from '@mfec/lit/loadFontsIntoLightDom';
import type { State } from '@mfec/lit/store';
import { StateController } from '@mfec/lit/store';
import type { TemplateTheme } from '@mfec/lit/TemplateResolver';
import type { Config } from '@mfec/types/tefComs';
import { assert } from '@mfec/utils/assert';
import { LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';

import { TriggerEventMixin, TriggerEventMixinI } from './TriggerEventMixin';

export interface StateControllerList {
  [key: string] : StateController<State>;
}

type Constructor<T = object> = new (...args: any[]) => T; // eslint-disable-line @typescript-eslint/no-explicit-any

export declare class StandardMicrofrontendMixinI extends TriggerEventMixinI {
  [key: string | symbol]: any; // eslint-disable-line 

  static version : string; // eslint-disable-line 

  cloak: boolean;
  accessToken?: string;
  traceid?: string;
  themeBaseUrl: string;
  theme: string;
  stateController : StateControllerList;

  setupStore(store: State): void;
  updateConfigStoreValues(changedProperties: Map<PropertyKey, unknown>): void;
  loadThemeResources() : void;
  handleResourceLoaded(): void;
  handleResourceError(event : Event): void;
}

export interface ConfigStore extends State {
  [key: string | symbol]: unknown;
  accessToken?: string;
  theme: TemplateTheme;
  themeBaseUrl: string;
  traceid?: string;
}

export const StandardMicrofrontendMixin = <T extends Constructor<LitElement>>(superClass: T, configStore : ConfigStore, config : Config) => {
  class StandardMicrofrontendMixinClass extends TriggerEventMixin(superClass, config.componentTagName) {
  // See https://www.typescriptlang.org/docs/handbook/2/classes.html#index-signatures
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string | symbol]: any;

    // These properties are controllable from the outside via HTML attributes eg. on the components tag
    @property({ type: String, attribute: 'theme' }) theme! : TemplateTheme;
    @property({ type: String, attribute: 'theme-base-url' }) themeBaseUrl! : string;
    @property({ type: String, attribute: 'access-token' }) accessToken? : string | undefined;
    @property({ type: String, attribute: 'traceid' }) traceid? : string | undefined;

    // By default the complete component is invisible until all resources are loaded.
    // To "reserve" the space look up the approximate height of the finished rendered component (via devtools)
    // and set the corresponding CSS var in TefComs4p.css
    @state() cloak : boolean = true;

    // Tracks the number of loaded resources
    @state() loadedResourcesCount : number = 0;
    @state() totalResourcesCount : number = 0;

    stateController : StateControllerList = {};
    #errorEventTriggered : boolean = false;

    static readonly version : string = _MFE_VERSION; // Filled/replaced by esbuild (see scripts/esbuild/*.ts)

    constructor() {
      super();
      this.setupStore(configStore);
    }

    override connectedCallback() {
    // Required attributes must be present on the element:
      assert(!!this.themeBaseUrl, 'missing required html attribute "theme-base-url"');
      assert(Object.keys(config.theme).includes(this.theme), `html attribute "theme" must be one of ["${Object.keys(config.theme).join('","')}"]`);

      // Call super only AFTER validating all required attributes!
      super.connectedCallback();

      // @ts-expect-error "Don't know how to type this"
      this.updateConfigStoreValues(this.constructor.elementProperties as Map<PropertyKey, unknown>);
      this.loadThemeResources();
    }

    setupStore(store : State) {
      const storeName = store.constructor.name.substring(0, 1).toLowerCase() + store.constructor.name.substring(1);
      // Reset store just to be sure
      store.reset();

      // Keep reference to statecontroller
      this.stateController[storeName] = new StateController(this, store);
    }

    // Update the config store with all changed @property
    updateConfigStoreValues(changedProperties: Map<PropertyKey, unknown>) {
      changedProperties.forEach((_value : unknown, key : PropertyKey) => {
      // Write known key/values to configStore
        if (configStore.propertyMap.has(key) && typeof this[key] !== 'undefined') {
          configStore[key] = this[key]; // eslint-disable-line no-param-reassign
        }
      });
    }

    // Load all configured resources into the component
    // See src/config/index.ts
    loadThemeResources() {
    // Only activate if both values are known
      if (configStore.theme && configStore.themeBaseUrl) {
        const themeConfig = config.theme[configStore.theme];
        if (!themeConfig) return;

        const fonts = themeConfig.fonts || [];
        this.totalResourcesCount = themeConfig.resources.length + fonts.length;

        // The OCCL font files are only available at the "o2" branded url for whatever reason
        // eg. https://dev-library.telefonica.de/static/1/fonts/OnAir-Regular.woff2
        // We need to fix that here..
        let fontLoadingBaseUrl = configStore.themeBaseUrl;
        if (configStore.theme === 'occl') {
          fontLoadingBaseUrl = fontLoadingBaseUrl.replace(/\d+$/, '1'); // eslint-disable-line sonarjs/slow-regex
        }

        // eslint-disable-next-line no-void
        void loadFontsIntoLightDom(
          fonts,
          fontLoadingBaseUrl,
          this.handleResourceLoaded.bind(this) as CallableFunction,
        );

        // Load external resources into shadowDOM
        injectExternalResources({
          config,
          parentElement: this.renderRoot,
          theme: configStore.theme,
          themeBaseUrl: configStore.themeBaseUrl,
          onErrorHandler: (e) => { (this.handleResourceError.bind(this) as CallableFunction)(e); },
          onLoadHandler: this.handleResourceLoaded.bind(this) as EventListener,
        });
      }
    }

    // Counts the loaded resources and uncloaks the component if all is loaded
    handleResourceLoaded() {
      this.loadedResourcesCount += 1;
      // Loaded all resources -> uncloak component
      if (this.loadedResourcesCount >= this.totalResourcesCount) {
        this.cloak = false;
      }
    }

    // eport an error if resource cannot load
    handleResourceError(event : Event) {
      if (this.#errorEventTriggered) return;

      const resource = (event.target! as HTMLElement).getAttribute('src') ?? (event.target! as HTMLElement).getAttribute('href');

      this.triggerEvent({
        name: 'load/ERROR', level: 'error', message: `Loading of resource "${resource}" failed`,
      });
      this.#errorEventTriggered = true;
    }
  }

  return StandardMicrofrontendMixinClass as Constructor<StandardMicrofrontendMixinI> & T;
};
